import React, { useEffect, useState } from "react";
import { PageHeader } from "@/components/Layout";
import { api, fmtDuration, fmtDateTime } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { UsersThree, Pulse, Trash, ClockCounterClockwise, Calculator, CurrencyDollar } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function TimeHub() {
  const [summary, setSummary] = useState({ total_clocked_in: 0, team: [] });
  const [entries, setEntries] = useState([]);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [filterUser, setFilterUser] = useState(null);

  // Payroll calculator
  const [payUser, setPayUser] = useState("");
  const [payStart, setPayStart] = useState("");
  const [payEnd, setPayEnd] = useState("");
  const [payRate, setPayRate] = useState("");
  const [payResult, setPayResult] = useState(null);
  const [payBusy, setPayBusy] = useState(false);

  const loadSummary = () => api.timeSummary().then(setSummary).catch(() => {});
  const loadEntries = () => api.allTime({ user_id: filterUser || undefined, start: start || undefined, end: end || undefined }).then(setEntries).catch(() => {});

  useEffect(() => { loadSummary(); }, []);
  useEffect(() => { loadEntries(); }, [filterUser, start, end]); // eslint-disable-line

  const calcPayroll = async () => {
    if (!payUser) return toast.error("Select an employee");
    if (!payStart || !payEnd) return toast.error("Pick a start and end date");
    if (payEnd < payStart) return toast.error("End date must be after start date");
    setPayBusy(true);
    try {
      const rows = await api.allTime({ user_id: payUser, start: payStart, end: payEnd });
      const completed = rows.filter((r) => r.clock_out);
      const minutes = completed.reduce((s, r) => s + (r.duration_minutes || 0), 0);
      setPayResult({ minutes, shifts: completed.length, openShifts: rows.length - completed.length });
    } catch { toast.error("Could not calculate hours"); }
    finally { setPayBusy(false); }
  };
  const payHours = payResult ? payResult.minutes / 60 : 0;
  const grossPay = payResult && payRate ? payHours * Number(payRate) : null;
  const payUserName = summary.team.find((t) => t.user_id === payUser)?.user_name;

  const remove = async (e) => { if (!window.confirm("Delete this time entry?")) return; await api.deleteTimeEntry(e.id); toast.success("Deleted"); loadEntries(); loadSummary(); };

  const activeTeam = summary.team.filter((t) => t.active);

  return (
    <div className="p-8 max-w-[1400px]">
      <PageHeader title="Time Hub" subtitle="Everyone's hours & clock in/out activity" />

      {/* Payroll calculator */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-5 mb-6" data-testid="payroll-calculator">
        <div className="flex items-center gap-2 mb-4">
          <Calculator size={18} weight="duotone" className="text-[#00B4D8]" />
          <h3 className="font-display font-semibold text-slate-900">Payroll Calculator</h3>
          <span className="text-xs text-slate-400">Total hours worked for one employee between two dates</span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-56">
            <label className="text-[11px] text-slate-500 block mb-1">Employee</label>
            <Select value={payUser} onValueChange={(v) => { setPayUser(v); setPayResult(null); }}>
              <SelectTrigger className="h-9" data-testid="payroll-employee"><SelectValue placeholder="Select employee" /></SelectTrigger>
              <SelectContent>
                {summary.team.map((t) => <SelectItem key={t.user_id} value={t.user_id}>{t.user_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><label className="text-[11px] text-slate-500 block mb-1">From</label><Input type="date" value={payStart} onChange={(e) => { setPayStart(e.target.value); setPayResult(null); }} className="h-9 w-40" data-testid="payroll-start" /></div>
          <div><label className="text-[11px] text-slate-500 block mb-1">To</label><Input type="date" value={payEnd} onChange={(e) => { setPayEnd(e.target.value); setPayResult(null); }} className="h-9 w-40" data-testid="payroll-end" /></div>
          <div>
            <label className="text-[11px] text-slate-500 block mb-1">Hourly rate <span className="text-slate-300">(optional)</span></label>
            <div className="relative">
              <CurrencyDollar size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input type="number" value={payRate} onChange={(e) => setPayRate(e.target.value)} placeholder="0.00" className="h-9 w-28 pl-7" data-testid="payroll-rate" />
            </div>
          </div>
          <Button onClick={calcPayroll} disabled={payBusy} className="h-9 bg-[#00B4D8] hover:bg-cyan-600" data-testid="payroll-calculate">{payBusy ? "Calculating…" : "Calculate"}</Button>
        </div>

        {payResult && (
          <div className="mt-5 pt-5 border-t border-slate-100 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="payroll-result">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400">Total Hours</div>
              <div className="font-display text-3xl font-extrabold text-[#00B4D8]" data-testid="payroll-hours">{payHours.toFixed(2)}</div>
              <div className="text-xs text-slate-400">{fmtDuration(payResult.minutes)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400">Shifts</div>
              <div className="font-display text-3xl font-extrabold text-slate-800">{payResult.shifts}</div>
              {payResult.openShifts > 0 && <div className="text-xs text-amber-600">{payResult.openShifts} still clocked in (not counted)</div>}
            </div>
            {grossPay != null && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400">Gross Pay</div>
                <div className="font-display text-3xl font-extrabold text-emerald-600" data-testid="payroll-gross">${grossPay.toFixed(2)}</div>
                <div className="text-xs text-slate-400">@ ${Number(payRate).toFixed(2)}/hr</div>
              </div>
            )}
            <div className="md:col-span-1 col-span-2 flex items-end">
              <div className="text-xs text-slate-500">{payUserName} · {payStart} → {payEnd}</div>
            </div>
          </div>
        )}
      </div>


      {/* Currently on the clock */}
      <div className="bg-[#0B1726] rounded-lg p-5 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Pulse size={18} weight="duotone" className="text-[#00B4D8]" />
          <h3 className="text-white font-display font-semibold">On the Clock Now</h3>
          <span className="ml-1 bg-[#00B4D8] text-white text-xs font-bold px-2 py-0.5 rounded-full" data-testid="active-count">{summary.total_clocked_in}</span>
        </div>
        {activeTeam.length === 0 ? (
          <p className="text-slate-400 text-sm">Nobody is currently clocked in.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {activeTeam.map((t) => (
              <div key={t.user_id} className="bg-white/5 border border-white/10 rounded-md p-3 flex items-center gap-3" data-testid={`active-user-${t.user_id}`}>
                <div className="w-9 h-9 rounded-full bg-slate-700 flex items-center justify-center text-sm font-bold text-white overflow-hidden shrink-0">
                  {t.avatar_url ? <img src={t.avatar_url} alt="" className="w-full h-full object-cover" /> : t.user_name?.[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white font-semibold text-sm truncate">{t.user_name}</div>
                  <div className="text-slate-400 text-xs">Since {fmtDateTime(t.clocked_in_since)}</div>
                </div>
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Per-person totals */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <UsersThree size={18} weight="duotone" className="text-[#00B4D8]" />
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Total Logged Hours</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {summary.team.map((t) => (
            <button key={t.user_id} onClick={() => setFilterUser(filterUser === t.user_id ? null : t.user_id)}
              className={`text-left bg-white border rounded-lg p-4 shadow-sm transition-colors duration-200 ${filterUser === t.user_id ? "border-[#00B4D8] ring-2 ring-[#00B4D8]/30" : "border-slate-200 hover:border-slate-300"}`}
              data-testid={`team-total-${t.user_id}`}>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-900 truncate">{t.user_name}</span>
                {t.active && <span className="w-2 h-2 rounded-full bg-emerald-400" />}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 capitalize mt-0.5">{t.user_role}</div>
              <div className="font-display text-2xl font-extrabold text-[#00B4D8] mt-2">{fmtDuration(t.total_minutes)}</div>
              <div className="text-xs text-slate-400">{t.entry_count} shifts</div>
            </button>
          ))}
        </div>
      </div>

      {/* Entry log */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="flex items-center gap-2">
          <ClockCounterClockwise size={18} weight="duotone" className="text-slate-500" />
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Activity Log</h3>
        </div>
        <div className="flex items-end gap-2 ml-auto">
          <div><label className="text-[11px] text-slate-500 block mb-1">From</label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-9 w-40" data-testid="hub-start" /></div>
          <div><label className="text-[11px] text-slate-500 block mb-1">To</label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-9 w-40" data-testid="hub-end" /></div>
          {(start || end || filterUser) && <Button variant="outline" className="h-9" onClick={() => { setStart(""); setEnd(""); setFilterUser(null); }}>Clear</Button>}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <TableHead>Employee</TableHead><TableHead>Clock In</TableHead><TableHead>Clock Out</TableHead>
            <TableHead>Duration</TableHead><TableHead>Job</TableHead><TableHead className="w-10"></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {entries.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-10">No time entries for this filter.</TableCell></TableRow>}
            {entries.map((e) => (
              <TableRow key={e.id} className="hover:bg-slate-50" data-testid={`hub-entry-${e.id}`}>
                <TableCell>
                  <div className="font-semibold text-slate-900">{e.user_name}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 capitalize">{e.user_role}</div>
                </TableCell>
                <TableCell className="text-sm">{fmtDateTime(e.clock_in)}</TableCell>
                <TableCell className="text-sm">{e.clock_out ? fmtDateTime(e.clock_out) : <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />Active</span>}</TableCell>
                <TableCell className="font-semibold">{fmtDuration(e.duration_minutes)}</TableCell>
                <TableCell className="text-sm text-slate-500">{e.job_number || "—"}</TableCell>
                <TableCell><button onClick={() => remove(e)} className="text-slate-300 hover:text-rose-500 transition-colors duration-200" data-testid={`delete-entry-${e.id}`}><Trash size={16} /></button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
